/* eslint-disable no-undef */
import React, { useState, useEffect, useMemo } from "react";
import { fmtMoney, fmtMoney0, fmtMonth, timeAgo, Icon, Sparkline, Pill, Avatar, initials, useToast, ApiDown } from "./components.jsx";
import { DATA } from "./data.js";
import { fetchUnits, fetchSensors, fetchInvoices, fetchBillPreview, fetchManagers, fetchProperties, mapUnit, mapSensor, mapInvoice, mapBillRow, syncMonnit } from "./api.js";
/* =============================================================================
 * Dashboard / Overview
 * ===========================================================================*/

function buildAttention(mgr) {
  const units = DATA.unitsFor(mgr).filter((u) => u.active);
  const sensors = DATA.sensorsFor(mgr);
  const invoices = DATA.invoicesFor(mgr);
  const items = [];

  // QBO reconnect
  DATA.managers.filter((m) => mgr === "all" || m.id === mgr).forEach((m) => {
    if (!m.qbo.connected) items.push({ sev: "danger", icon: "link", title: `${m.name} — QuickBooks disconnected`, sub: "Token refresh failed. Invoices can't be sent until reconnected.", action: "Reconnect", route: "connections" });
    else if (m.qbo.tokenDaysLeft < 20) items.push({ sev: "warn", icon: "clock", title: `${m.name} — QuickBooks token expiring`, sub: `Refresh token expires in ${m.qbo.tokenDaysLeft} days.`, action: "Review", route: "connections" });
  });
  // offline sensors
  sensors.filter((s) => s.offline).forEach((s) => {
    const u = DATA.unit(s.unitIds[0]);
    items.push({ sev: "danger", icon: "sensor", title: `Sensor ${s.sensorId} not reporting`, sub: `${s.name} · last seen ${timeAgo(s.lastComm)}. Readings may be stale.`, action: "View", route: "sensors", sensorId: s.sensorId });
  });
  // low battery
  sensors.filter((s) => s.lowBatt && !s.offline).forEach((s) => {
    items.push({ sev: "warn", icon: "battery", title: `Sensor ${s.sensorId} battery low — ${s.battery}%`, sub: `${s.name}. Schedule a battery replacement.`, action: "View", route: "sensors", sensorId: s.sensorId });
  });
  // usage spikes (possible leak) in pending period
  units.filter((u) => u.pendingRead && u.pendingRead.spike).forEach((u) => {
    items.push({ sev: "warn", icon: "droplet", title: `Usage spike — ${u.name}`, sub: `${u.tenant} used ${u.pendingUsage} units this period, well above normal. Possible leak.`, action: "Inspect", route: "properties", unitId: u.id });
  });
  // overdue invoices
  const overdue = invoices.filter((i) => i.status === "Overdue");
  if (overdue.length) items.push({ sev: "danger", icon: "receipt", title: `${overdue.length} overdue invoice${overdue.length > 1 ? "s" : ""}`, sub: `${fmtMoney(overdue.reduce((s, i) => s + i.total, 0))} outstanding across ${new Set(overdue.map((i) => i.unitId)).size} units.`, action: "View", route: "invoices" });

  const order = { danger: 0, warn: 1, info: 2 };
  return items.sort((a, b) => order[a.sev] - order[b.sev]);
}

// Live counterpart of buildAttention: only alerts we have real sources for.
function buildLiveAttention(sensors, draftCount, syncRef) {
  const items = [];
  // Only metering sensors (attached to a unit) raise alerts — retired/spare devices being offline is expected.
  sensors.filter((s) => s.offline && !s.retired).forEach((s) => {
    items.push({ sev: "danger", icon: "sensor", title: `Sensor ${s.sensorId} not reporting`, sub: `${s.name} · last seen ${s.lastComm ? timeAgo(s.lastComm, syncRef) : "never"}. Readings may be stale.`, action: "View", route: "sensors", sensorId: s.sensorId });
  });
  sensors.filter((s) => s.lowBatt && !s.offline && !s.retired).forEach((s) => {
    items.push({ sev: "warn", icon: "battery", title: `Sensor ${s.sensorId} battery low — ${s.battery}%`, sub: `${s.name}. Schedule a battery replacement.`, action: "View", route: "sensors", sensorId: s.sensorId });
  });
  if (draftCount > 0) {
    items.push({ sev: "info", icon: "receipt", title: `${draftCount} draft invoice${draftCount > 1 ? "s" : ""} pending QuickBooks push`, sub: "QuickBooks OAuth connection is the next build step — drafts are saved locally.", action: "View", route: "invoices" });
  }
  const order = { danger: 0, warn: 1, info: 2 };
  return items.sort((a, b) => order[a.sev] - order[b.sev]);
}

const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

function Dashboard({ mgr, go, openUnit, setMgr }) {
  const [live, setLive] = useState(null);   // {units,sensors,invoices,preview,managers,properties} | false | null=loading
  const toast = useToast();
  const [syncing, setSyncing] = useState(false);
  const runSync = () => {
    setSyncing(true);
    syncMonnit()
      .then((r) => toast({ title: r.hasCredentials ? "Monnit sync started" : "Sync queued — no token yet", body: r.hasCredentials ? `Job #${r.jobId} is pulling sensors & readings…` : "Add a Monnit token to config to pull live data." }))
      .catch(() => toast({ title: "Sync failed", body: "Couldn't reach the API." }))
      .finally(() => setSyncing(false));
  };

  useEffect(() => {
    let c = false;
    const mid = mgr === "all" ? null : mgr;
    Promise.all([fetchUnits(), fetchSensors(mid), fetchInvoices(mid), fetchBillPreview(mid, isoToday()), fetchManagers(), fetchProperties()])
      .then(([u, s, inv, pv, mg, pr]) => {
        if (c) return;
        const newestMs = Math.max(0, ...s.map((x) => (x.lastComm ? new Date(x.lastComm).getTime() : 0)));
        setLive({
          units: u.map(mapUnit).filter((x) => x.active && (mgr === "all" || x.managerId === mgr)),
          sensors: s.map((x) => mapSensor(x, newestMs)),
          invoices: inv.map(mapInvoice),
          preview: pv.map(mapBillRow),
          managers: mg,
          properties: pr,
        });
      })
      .catch(() => { if (!c) setLive(false); });
    return () => { c = true; };
  }, [mgr]);

  const usingLive = !!live;

  // ---- shared derivations (live or mock) ----
  const invoices = usingLive ? live.invoices : DATA.invoicesFor(mgr);
  const preview = usingLive ? live.preview : DATA.billRunPreview(mgr, DATA.NOW);
  const eligible = preview.filter((p) => p.eligible);
  const pendingRevenue = eligible.reduce((s, p) => s + p.comp.total, 0);
  const overdue = invoices.filter((i) => i.status === "Overdue");
  const sentUnpaid = invoices.filter((i) => i.status === "Open" || i.status === "Sent");   // "Sent" = mock-data compat
  const drafts = usingLive ? invoices.filter((i) => i.status === "Draft") : [];
  const sensors = usingLive ? live.sensors : DATA.sensorsFor(mgr);
  // Sensor-health KPI counts only assigned pulse-counter meters — unassigned spares & non-meter devices excluded (matches the Sensors screen).
  const assignedMeters = sensors.filter((s) => s.isPulseCounter !== false && !s.retired);
  const sensorIssues = assignedMeters.filter((s) => s.alertsActive).length;
  // sensor "last seen" is measured against the snapshot's newest reading (last sync), like the Sensors screen
  const sensorSyncRef = usingLive ? new Date(Math.max(0, ...sensors.map((s) => (s.lastComm ? s.lastComm.getTime() : 0)))) : DATA.NOW;
  const attention = usingLive ? buildLiveAttention(sensors, drafts.length, sensorSyncRef) : buildAttention(mgr);
  const NOW = usingLive ? new Date() : DATA.NOW;

  // billed this cycle + outstanding. "Billed" = issued and still standing — drafts aren't billed
  // yet, and a voided invoice was un-billed (it shouldn't keep inflating the KPI or trend).
  // CALENDAR month of the current cycle, the same bucket as the trend's last point — a rolling
  // window here quietly mixed two cycles and disagreed with the trend on the same screen.
  const isBilled = (i) => i.status !== "Draft" && i.status !== "Void";
  const cycleStart = new Date(NOW.getFullYear(), NOW.getMonth(), 1);
  const recent = invoices.filter((i) => isBilled(i) && i.date >= cycleStart);
  const billedThisMonth = recent.reduce((s, i) => s + i.total, 0);
  const unpaidTotal = sentUnpaid.concat(overdue).reduce((s, i) => s + i.total, 0);

  // monthly revenue trend (last 6 months) for sparkline — bucketed by INVOICE DATE, the same "billed" basis
  // as the "Billed last cycle" KPI (bucketing by period end made the two disagree 11x on the same screen).
  const trend = useMemo(() => {
    const buckets = {};
    invoices.filter(isBilled).forEach((i) => {
      const d = i.date || i.periodEnd;
      const k = d.getFullYear() * 12 + d.getMonth();
      buckets[k] = (buckets[k] || 0) + i.total;
    });
    const keys = Object.keys(buckets).map(Number).sort((a, b) => a - b).slice(-6);
    return { points: keys.map((k) => buckets[k]), lastMonth: keys.length ? new Date(Math.floor(keys[keys.length - 1] / 12), keys[keys.length - 1] % 12, 1) : null };
  }, [invoices]);

  // activity: live = recent invoice creations; mock = canned log
  const activity = usingLive
    ? invoices.slice(0, 7).map((i) => ({ id: i.id, source: "Billing", level: "Info", message: `Invoice #${i.number} created — ${fmtMoney(i.total)} · ${i.tenant}`, time: i.date }))
    : DATA.activity.slice(0, 7);

  const scopeName = mgr === "all"
    ? "All property managers"
    : usingLive ? ((live.managers.find((m) => m.id === mgr) || {}).name || "Manager") : ((DATA.manager(mgr) || {}).name || "Manager");

  const KPI = ({ label, icon, iconBg, value, sub, accent }) => (
    <div className="kpi" style={accent ? { background: accent, color: "#fff" } : {}}>
      <div className="kpi__label" style={accent ? { color: "rgba(255,255,255,.85)" } : {}}>
        <span className="ic" style={{ background: accent ? "rgba(255,255,255,.18)" : iconBg, color: accent ? "#fff" : "var(--color-primary)" }}><Icon name={icon} size={17} /></span>
        {label}
      </div>
      <div className="kpi__value">{value}</div>
      <div className="kpi__sub" style={accent ? { color: "rgba(255,255,255,.8)" } : {}}>{sub}</div>
    </div>
  );

  // Deployed builds: an API failure must be an honest error, not mock data that looks live.
  // Placed after every hook above (incl. the trend useMemo) so hook order is stable.
  if (!live && !import.meta.env.DEV) return <ApiDown loading={live !== false} />;

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>Overview</h1>
          <p>{scopeName} · billing cycle {fmtMonth(NOW)}{usingLive ? " · Live (API)" : live === false ? " · mock (API offline)" : ""}</p>
        </div>
        <div className="right">
          <button className="btn btn--ghost" disabled={syncing} onClick={runSync}><Icon name="refresh" size={16} />{syncing ? "Syncing…" : "Sync Monnit"}</button>
          <button className="btn btn--primary" onClick={() => go("invoices", { create: true })}><Icon name="receipt" size={16} />Create invoices</button>
        </div>
      </div>

      <div className="kpis">
        <KPI label="Ready to bill" icon="play" iconBg="var(--color-primary-tint)"
          value={<span>{eligible.length}<small> / {preview.length} units</small></span>}
          sub={<span><b>{fmtMoney0(pendingRevenue)}</b> projected this run</span>} accent={"var(--accent)"} />
        <KPI label={`Billed · ${fmtMonth(NOW)}`} icon="receipt" iconBg="var(--color-primary-tint)"
          value={fmtMoney0(billedThisMonth)} sub={<span>{recent.length} invoice{recent.length === 1 ? "" : "s"} issued this cycle</span>} />
        <KPI label="Awaiting payment" icon="clock" iconBg="var(--color-warning-bg)"
          value={fmtMoney0(unpaidTotal)}
          sub={usingLive && drafts.length
            ? <span><b>{drafts.length}</b> drafts pending QBO push</span>
            : <span>{sentUnpaid.length} open · <b style={{ color: "var(--color-danger)" }}>{overdue.length} overdue</b></span>} />
        <KPI label="Sensor health" icon="sensor" iconBg="var(--color-success-bg)"
          value={<span>{assignedMeters.length - sensorIssues}<small> / {assignedMeters.length} ok</small></span>}
          sub={sensorIssues ? <span style={{ color: "var(--color-danger)" }}>{sensorIssues} need attention</span> : <span>All reporting</span>} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.35fr) minmax(0,1fr)", gap: 16, alignItems: "start" }}>
        {/* Needs attention */}
        <div className="card" style={{ padding: 0 }}>
          <div className="between" style={{ padding: "16px 20px 12px" }}>
            <h3 style={{ margin: 0, fontSize: "var(--font-size-lg)", fontWeight: 700 }}>Needs attention</h3>
            <span className="tag">{attention.length} item{attention.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="attn" style={{ padding: "0 20px 8px" }}>
            {attention.length === 0 && <div className="empty">Nothing needs attention. Everything is on track.</div>}
            {attention.map((a, i) => {
              const bg = a.sev === "danger" ? "var(--color-danger-bg)" : a.sev === "warn" ? "var(--color-warning-bg)" : "var(--color-primary-tint)";
              const fg = a.sev === "danger" ? "var(--color-danger)" : a.sev === "warn" ? "var(--color-warning-text)" : "var(--color-primary)";
              return (
                <div className="attn__item" key={i}>
                  <span className="attn__ic" style={{ background: bg, color: fg }}><Icon name={a.icon} size={18} /></span>
                  <div className="attn__txt"><b>{a.title}</b><span>{a.sub}</span></div>
                  <div className="attn__act">
                    <button className="btn btn--ghost btn--sm" onClick={() => {
                      if (a.unitId) openUnit(a.unitId);
                      else go(a.route, a.sensorId ? { sensorId: a.sensorId } : undefined);
                    }}>{a.action}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Revenue + activity */}
        <div style={{ display: "grid", gap: 16 }}>
          <div className="card">
            <div className="between" style={{ marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: "var(--font-size-lg)", fontWeight: 700 }}>Revenue trend</h3>
              {trend.points.length >= 2 && <span className="pill pill--ok"><Icon name="trend" size={12} /> {trend.points.length} mo</span>}
            </div>
            <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>{fmtMoney0(trend.points[trend.points.length - 1] || 0)}</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>billed in {trend.lastMonth ? fmtMonth(trend.lastMonth) : "—"}</div>
            {trend.points.length >= 2 ? (
              <>
                <Sparkline data={trend.points} w={300} h={56} />
                <div className="row" style={{ justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "var(--color-muted)" }}>
                  <span>{trend.points.length} months ago</span><span>{trend.lastMonth ? fmtMonth(trend.lastMonth) : fmtMonth(NOW)}</span>
                </div>
              </>
            ) : (
              <div className="muted" style={{ fontSize: 12, padding: "6px 0 2px" }}>Only one billing cycle on record — the trend appears once there are at least two.</div>
            )}
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div className="between" style={{ padding: "16px 20px 10px" }}>
              <h3 style={{ margin: 0, fontSize: "var(--font-size-lg)", fontWeight: 700 }}>Activity log</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => go("invoices")}>View all</button>
            </div>
            <div style={{ padding: "0 20px 14px", maxHeight: 230, overflowY: "auto" }}>
              {activity.length === 0 && <div className="empty">No recent activity.</div>}
              {activity.map((l) => (
                <div key={l.id} className="row" style={{ padding: "9px 0", borderBottom: "1px solid var(--color-border-light)", alignItems: "flex-start", gap: 10 }}>
                  <span className={`pill ${l.level === "Error" ? "pill--danger" : l.level === "Warning" ? "pill--warn" : "pill--muted"}`} style={{ marginTop: 1 }}>{l.source}</span>
                  <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.35 }}>{l.message}<div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{timeAgo(l.time, NOW)}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio snapshot (only on All view) */}
      {mgr === "all" && (
        <div className="card" style={{ marginTop: 16, padding: 0 }}>
          <div className="between" style={{ padding: "16px 20px 12px" }}>
            <h3 style={{ margin: 0, fontSize: "var(--font-size-lg)", fontWeight: 700 }}>Property managers</h3>
            <span className="tag">{(usingLive ? live.managers : DATA.managers).length} accounts</span>
          </div>
          <table className="tbl">
            <thead><tr><th>Manager</th><th>Properties</th><th>Active units</th><th>Ready to bill</th><th className="num">Projected</th><th>QuickBooks</th></tr></thead>
            <tbody>
              {usingLive
                ? live.managers.map((m) => {
                    const mu = live.units.filter((u) => u.managerId === m.id);
                    const mp = live.properties.filter((p) => p.managerId === m.id);
                    const pv = live.preview.filter((p) => p.eligible && p.unit.managerId === m.id);
                    return (
                      <tr key={m.id} style={{ cursor: "pointer" }} title={`Drill into ${m.name}'s properties & units`} onClick={() => { setMgr && setMgr(m.id); go("properties"); }}>
                        <td><div className="row"><Avatar id={m.id} label={initials(m.name)} /><div className="cell-2"><b>{m.name}</b></div></div></td>
                        <td>{mp.length}</td>
                        <td>{mu.length}</td>
                        <td>{pv.length}</td>
                        <td className="num strong">{fmtMoney0(pv.reduce((s, p) => s + p.comp.total, 0))}</td>
                        <td>{m.qboConnected ? <Pill kind="ok">Connected</Pill> : <Pill kind="muted">Not linked</Pill>}</td>
                      </tr>
                    );
                  })
                : DATA.managers.map((m) => {
                    const mu = DATA.unitsFor(m.id).filter((u) => u.active);
                    const mp = DATA.propertiesFor(m.id);
                    const pv = DATA.billRunPreview(m.id, DATA.NOW).filter((p) => p.eligible);
                    return (
                      <tr key={m.id} style={{ cursor: "pointer" }} title={`Drill into ${m.name}'s properties & units`} onClick={() => { setMgr && setMgr(m.id); go("properties"); }}>
                        <td><div className="row"><Avatar id={m.id} label={initials(m.name)} /><div className="cell-2"><b>{m.name}</b><span>Realm {m.realmId}</span></div></div></td>
                        <td>{mp.length}</td>
                        <td>{mu.length}</td>
                        <td>{pv.length}</td>
                        <td className="num strong">{fmtMoney0(pv.reduce((s, p) => s + p.comp.total, 0))}</td>
                        <td>{m.qbo.connected ? <Pill kind="ok">Connected</Pill> : <Pill kind="danger">Disconnected</Pill>}</td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { Dashboard, buildAttention };
